import { createSelector } from 'reselect';

import { CAMPAIGN_FILE_STATUS } from 'components/campaigns/Details/constants';
import { SALES_OFFER_CHECKLISTS } from 'constants/ChecklistConstants';
import { ENTITY_TYPE } from 'modules/app/constants';
import { CAMPAIGN_FILE_EVENT_ENUM, DEFAULT_SORT } from 'modules/campaignFiles/constants';
import { getCampaignById } from 'modules/campaigns/selectors';
import { getSelectedCampaignMode } from 'modules/campaigns/selectors/details';
import { filterRequestFormatter } from 'modules/interventions/utils';

const getRoot = state => state;

const getProps = (_, props) => props;

/** @return {import("../reducers").CampaignReducer} */
const getState = state => state.campaignFiles;

const getDataState = createSelector(getState, state => state.data);

const getDataEntities = createSelector(getDataState, state => state.entities);

export const getUIState = createSelector(getState, state => state.ui);

export const getCampaignFilesByIds = createSelector(getDataEntities, getProps, (entities, ids = []) =>
  ids.reduce((acc, campaignFileId) => {
    const entity = entities[campaignFileId];
    if (entity) {
      return [...acc, entity];
    }
    return acc;
  }, []));

const getUICampaignFiles = createSelector(getUIState, state => state.campaignFiles);

const getCampaignFilesIds = createSelector(getUICampaignFiles, state => state.ids);

export const getCampaignFilesLoading = createSelector(getUICampaignFiles, state => state.loading);

export const getCampaignFilesPage = createSelector(getUICampaignFiles, state => state.page);

export const getCampaignFiles = createSelector(getRoot, getCampaignFilesIds, getCampaignFilesByIds);

export const getCampaignFileById = createSelector(
  getDataEntities,
  getProps,
  (entities, campaignFileId) => entities[campaignFileId]
);

export const getFilters = createSelector(getUIState, state => state.filters);

export const getCampaignFilesParams = createSelector(
  getSelectedCampaignMode,
  getCampaignFilesPage,
  getFilters,
  (mode, { size, number }, filters) => ({
    size,
    mode,
    page: number,
    projection: 'page',
    sort: DEFAULT_SORT,
    ...filterRequestFormatter(filters),
  })
);

const getLoadingSlice = createSelector(getUIState, ui => ui.loading);

export const isHandling = createSelector(getLoadingSlice, getProps, (slice, id) => slice.handled.includes(id));

export const isCreatingChecklists = createSelector(getLoadingSlice, getProps, (slice, id) => slice.checklists.includes(id));

export const isApprovingManually = createSelector(getLoadingSlice, getProps, (slice, id) => slice.approving.includes(id));

export const isInternalMessagePatching = createSelector(getLoadingSlice, getProps, (slice, id) =>
  slice.internalMessage.includes(id));

export const isHandled = createSelector(getCampaignFileById, campaignFile => campaignFile.handled);

const getDetailsDrawerState = createSelector(getUIState, state => state.details);

export const isDetailsDrawerOpen = createSelector(getDetailsDrawerState, state => state.open);

export const getDetailsCampaignFileId = createSelector(getDetailsDrawerState, state => state.campaignFileId);

export const getDetailsCampaignFile = createSelector(getRoot, getDetailsCampaignFileId, getCampaignFileById);

export const getSelectedCampaignFileHasArchivedMedias = createSelector(getDetailsCampaignFile, ({ hasArchivedMedias }) => hasArchivedMedias ?? false);

export const getSelectedCampaignFileArchivedMediasCount = createSelector(
  getDetailsCampaignFile,
  ({ archivedMediaCount }) => archivedMediaCount ?? 0
);

export const getSelectedCampaignFileHasRestoringMediasFlag = createSelector(
  getDetailsCampaignFile,
  ({ hasRestoringMedias }) => hasRestoringMedias ?? false
);

export const getSortedChecklists = createSelector(getDetailsCampaignFile, campaignFile =>
  [...(campaignFile.checklists ?? [])].sort(
    (a, b) => SALES_OFFER_CHECKLISTS.indexOf(a.type) - SALES_OFFER_CHECKLISTS.indexOf(b.type)
  ));

export const getCampaignIdByCampaignFileId = createSelector(
  getCampaignFileById,
  (campaignFile) => campaignFile?.campaignId
);

export const getCampaignByCampaignFileId = createSelector(getRoot, getCampaignIdByCampaignFileId, getCampaignById);

export const canCreateIntervention = createSelector(
  getCampaignFileById,
  getCampaignByCampaignFileId,
  (campaignFile, campaign) => {
    if (!campaignFile || campaignFile.interventionId) {
      return false;
    }

    const isEmailReceived = [
      CAMPAIGN_FILE_EVENT_ENUM.RECEIVED,
      CAMPAIGN_FILE_EVENT_ENUM.OPENED,
      CAMPAIGN_FILE_EVENT_ENUM.CLICKED
    ].includes(campaignFile.insights?.EMAIL?.status);

    return campaign.hasProposedWorks ? campaignFile.status === CAMPAIGN_FILE_STATUS.ANSWERED : isEmailReceived;
  }
);

export const getSimpleFileInitialValues = createSelector(
  getCampaignFileById,
  getCampaignByCampaignFileId,
  getProps,
  (campaignFile, campaign, campaignFileId) => {
    const { id: campaignId, brand, brandId, dealerId, mode, module } = campaign;

    return {
      campaignId,
      campaignFileId,
      mode,
      module,
      dealerId,
      brandId: brandId ?? brand.id,
      referenceId: campaignFile.reference,
    };
  }
);

// This is the customer as it is stored in the campaign file, those information might differ from the one we have in the customer table
export const getCampaignFileCustomerInfo = createSelector(getDetailsCampaignFile, ({ customerInfo }) => customerInfo);

export const getCampaignFileSocketParameters = createSelector(
  getDetailsCampaignFileId,
  campaignFileId => ({
    entityType: ENTITY_TYPE.CAMPAIGN_FILE,
    entityId: campaignFileId,
  })
);
